import { Button } from '@ophelos/ui/components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ophelos/ui/components/dialog';
import { ChevronRight, Loader2 } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { AffordabilityCommitment } from '@/components/payments/AffordabilityCommitment';
import { MultibancoReference } from '@/components/payments/MultibancoReference';
import { DiscountAlert } from '@/components/payments/DiscountAlert';
import { PayerEmailField } from '@/components/payments/PayerEmailField';
import { isSameDay, PaymentDatePicker } from '@/components/payments/PaymentDatePicker';
import { usePaymentOptions } from '@/components/payments/PaymentOptions';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { BottomActionBar } from '@/components/layout/BottomActionBar';
import { useDebtData } from '@/contexts/DebtDataContext';
import { useTranslation } from '@/hooks/useTranslation';
import { applyCredit } from '@/lib/accountNet';
import { discountedAmountMillicents } from '@/lib/discount-offers';
import { formatMoneyInLanguage } from '@/lib/currency';
import type { PaymentMethod } from '@ophelos/shared/constants/payment-methods';
import { type MultibancoReference as MultibancoReferenceType } from '@/lib/payment-api';
import type { PaymentOptionsPlacement } from '@/types';

interface PayInFullTabProps {
  caseId: string;
  amount: number;
  /** Credit attributed to this debt, taken off after any discount. */
  creditApplied?: number;
  currency?: string;
  onContinue?: () => void;
  allowFullPayLater?: boolean; // If false, locks date to "today"
  maxDaysInFuture?: number;
  /** Reports the amount and date the tab currently shows, on mount and whenever either changes. */
  onSelectPaymentMethod?: (amount: number, type: 'full' | 'partial', date?: Date) => void;
  /** The method picker, owned by the page since availability depends on the date reported above. */
  methodSelector?: ReactNode;
  paymentMethod?: PaymentMethod;
  onPay?: () => void;
  isProcessing?: boolean;
  /** False while the chosen method cannot take this payment (provider off, date-gated). */
  canPay?: boolean;
  needsEmail?: boolean;
  payerEmail?: string;
  onPayerEmailChange?: (value: string) => void;
  emailValid?: boolean;
  /** Absent when the operator does not offer a plan on this debt, or a session in flight blocks one. */
  onChoosePlan?: () => void;
  /** Absent when the operator does not offer a part payment on this debt, or a session in flight blocks one. */
  onChoosePartial?: () => void;
  /** Live plan discount, shown as a badge on the plan button. */
  planDiscountLabel?: string | null;
  /** 'inline' lists the plan and part payment under the balance; 'menu' keeps them behind an Options button. */
  optionsPlacement?: PaymentOptionsPlacement;
}

export function PayInFullTab({
  caseId,
  amount,
  creditApplied = 0,
  currency = 'EUR',
  onContinue: _onContinue,
  allowFullPayLater = true,
  onSelectPaymentMethod,
  methodSelector,
  paymentMethod = 'card',
  onPay,
  isProcessing = false,
  canPay = true,
  maxDaysInFuture = 14,
  needsEmail = false,
  payerEmail = '',
  onPayerEmailChange,
  emailValid = true,
  onChoosePlan,
  onChoosePartial,
  planDiscountLabel = null,
  optionsPlacement = 'inline',
}: PayInFullTabProps) {
  const { t, currentLanguage } = useTranslation();
  const { activeOffers } = useDebtData();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [multibancoRef, setMultibancoRef] = useState<MultibancoReferenceType | null>(null);

  const offer = activeOffers[caseId];
  const currentAmountMillicents = Math.round(amount * 100_000);
  const discountedMillicents = offer ? discountedAmountMillicents(offer, 'pay_now', currentAmountMillicents) : null;
  const hasEligibleDiscount = offer != null && discountedMillicents != null && offer.status !== 'expired' && offer.status !== 'superseded';
  const showDiscount = hasEligibleDiscount;
  const discountedAmount = showDiscount ? (discountedMillicents as number) / 100_000 : amount;
  // The credit attributed to this debt comes off after the discount, matching the account
  // surfaces and the figure the dashboard card showed before the customer got here.
  const displayAmount = applyCredit(discountedAmount, creditApplied);
  const payNowPct = offer?.configSnapshot.options?.pay_now?.percentage ?? 0;
  const savingsAmount = showDiscount ? amount - discountedAmount : 0;

  const today = new Date();
  const maxDate = new Date();
  maxDate.setDate(today.getDate() + maxDaysInFuture);

  const isToday = selectedDate && isSameDay(selectedDate, today);
  const isScheduled = !!selectedDate && !isToday;

  const options = usePaymentOptions({ onChoosePlan, onChoosePartial, planDiscountLabel, placement: optionsPlacement });

  // The full-balance pay applies the active discount when one is live.
  useEffect(() => {
    onSelectPaymentMethod?.(displayAmount, 'full', isScheduled ? selectedDate : undefined);
  }, [onSelectPaymentMethod, displayAmount, selectedDate, isScheduled]);

  // Amount Selection Step
  return (
    <div className="w-full flex-1 min-h-0 stack items-center px-3 xs:px-4 max-w-lg mx-auto" data-testid="payment-view-full">
      {showDiscount && (
        <DiscountAlert
          title={t('discount.percentApplied', { pct: Math.round(payNowPct * 100) })}
          saving={
            <Trans
              i18nKey="discount.youSavePayInFull"
              components={{ amount: <CurrencyDisplay amount={savingsAmount} currency={currency} className="font-semibold inline-flex gap-0" /> }}
            />
          }
          expiresAt={offer?.expiresAt}
        />
      )}
      <div className="flex-1 stack gap-6 w-full">
        {/* The offer and its expiry in one line above the amount; the full price struck through beneath */}

        <div className={`flex flex-col items-center gap-2 ${showDiscount ? 'mt-10' : 'mt-18'}`}>
          {showDiscount && <CurrencyDisplay amount={amount} currency={currency} className="text-muted-foreground line-through" />}
          <div className="text-5xl font-bold">
            <CurrencyDisplay amount={displayAmount} currency={currency} className="text-5xl" centsClassName="text-2xl" />
          </div>
        </div>

        <div className="flex justify-center max-w-3xs mx-auto w-full">
          <PaymentDatePicker
            disabled={isProcessing}
            value={selectedDate}
            onChange={setSelectedDate}
            maxDate={maxDate}
            canDefer={allowFullPayLater}
            lockedCopyKey="paymentTabs.paymentMustBeToday"
            label={
              isToday
                ? t('paymentTabs.payToday')
                : selectedDate
                  ? `${t('paymentTabs.payOn')} ${selectedDate.toLocaleDateString()}`
                  : t('paymentTabs.selectDate')
            }
            data-testid="payment-action-date"
          />
        </div>

        {options.rows}

        {needsEmail && onPayerEmailChange && <PayerEmailField id="single-full-payer-email" value={payerEmail} onChange={onPayerEmailChange} />}

        <div className="w-full mt-8">{methodSelector}</div>

        <AffordabilityCommitment />
      </div>

      {/* Multibanco Reference Modal */}
      <Dialog open={!!multibancoRef} onOpenChange={(open) => !open && setMultibancoRef(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('payment.paymentReferenceTitle')}</DialogTitle>
          </DialogHeader>
          {multibancoRef && <MultibancoReference reference={multibancoRef} onClose={() => setMultibancoRef(null)} />}
        </DialogContent>
      </Dialog>

      {options.menu}

      <BottomActionBar>
        <div className="flex w-full items-center justify-center gap-4">
          {options.button}
          <Button onClick={onPay} size="xl" className="group" disabled={!emailValid || isProcessing || !canPay}>
            {isProcessing ? (
              <>
                <Loader2 className="size-5 mr-2 animate-spin" />
                {t('verify.processing')}
              </>
            ) : (
              <>
                {/* Multibanco issues a code to pay later at an ATM — nothing is charged
                    on this click, so the card's "Pay €X now" would misdescribe it. */}
                {paymentMethod === 'multibanco'
                  ? t('paymentMethod.getMultibancoReference')
                  : isScheduled
                    ? t('paymentTabs.schedulePayment')
                    : t('paymentTabs.payNow', { amount: formatMoneyInLanguage(displayAmount, currentLanguage, currency) })}
                <ChevronRight className="size-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </Button>
        </div>
      </BottomActionBar>
    </div>
  );
}
